import {
  AcademicCap,
  Brain,
  Trophy,
} from '@components/common/icons/custom-icons'
import { Course } from '@components/courses/course-card'
import { ElaCourseContent } from '@lib/types/ela-course'
import { defineMessage, Trans } from '@lingui/macro'
import {
  BarChart2,
  BookOpen,
  Calendar,
  Clock,
  MessageSquare,
  Mic,
  Play,
  Target,
  TrendingUp,
  Users,
  Volume2,
} from 'react-feather'

export const PSC_COURSES_GRADE_1_2: Record<
  string,
  { label: string; path: string }
> = {
  psc101_12: {
    label: 'Public Speaking & Communication 101',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_101_Grade_1-2-f037.pdf',
  },
  pscs_12: {
    label: 'Public Speaking & Communication Star',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_Star_Grade_1-2-e99f.pdf',
  },
  pscc_12: {
    label: 'Public Speaking & Communication Champion',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_Champion_Grade_1-2-f0e8.pdf',
  },
  pscp_12: {
    label: 'Public Speaking & Communication Prodigy',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_Prodigy_Grade_1-2-8a3a.pdf',
  },
  pscgm_12: {
    label: 'Public Speaking & Communication Grandmaster',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_Grandmaster_Grade_1-2-b3ae.pdf',
  },
}

export const PSC_COURSES_GRADE_6_8: Record<
  string,
  { label: string; path: string }
> = {
  psc101_6_8: {
    label: 'Public Speaking & Communication 101',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_speaking___Communication_101_Grade_6-8-99f9.pdf',
  },
  pscs_6_8: {
    label: 'Public Speaking & Communication Star',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_speaking___Communication_Star_Grade_6-8-2c18.pdf',
  },
  pscc_6_8: {
    label: 'Public Speaking & Communication Champion',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_speaking___Communication_Champion_Grade_6-8-7fcb.pdf',
  },
  pscp_6_8: {
    label: 'Public Speaking & Communication Prodigy',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_speaking___Communication_Prodigy_Grade_6-8-61cf.pdf',
  },
  pscgm_6_8: {
    label: 'Public Speaking & Communication Grandmaster',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_speaking___Communication_Grandmaster_Grade_6-8-8681.pdf',
  },
}

export const PSC_COURSES_GRADE_3_5: Record<
  string,
  { label: string; path: string }
> = {
  psc101_3_5: {
    label: 'Public Speaking & Communication 101',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_101_Grade_3-5-ec4e.pdf',
  },
  pscs_3_5: {
    label: 'Public Speaking & Communication Star',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_Star_Grade_3-5-4a27.pdf',
  },
  pscc_3_5: {
    label: 'Public Speaking & Communication Champion',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_Champion_Grade_3-5-51a3.pdf',
  },
  pscp_3_5: {
    label: 'Public Speaking & Communication Prodigy',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_Prodigy_Grade_3-5-c645.pdf',
  },
  pscgm_3_5: {
    label: 'Public Speaking & Communication Grandmaster',
    path:
      'https://codingal.s3.ap-south-1.amazonaws.com/media/blackops/curriculum-pdfs/Public_Speaking___Communication_Grandmaster_Grade_3-5-1a33.pdf',
  },
}

const PATHWAY_COURSES: Course[] = [
  {
    id: 1,
    title: 'Early Elementary Grades 1-2',
    description:
      'Introduce young learners to the joy of speaking and sharing ideas. Through fun stories, simple presentations, and interactive activities, children build early confidence in expressing themselves clearly and listening to others.',
    thumbnailLandscape:
      '/images/courses/public-speaking-and-communication/grade-1-2/listing-card.jpg',
    ageRange: 'Ages 5-7',
    gradeRange: 'Grades 1-2',
    estimatedCompletionTime: '1-18 months',
    studentsEnrolledCount: 0,
    totalLessonsCount: 144,
    tags: null,
    landingPageUrl: '/courses/public-speaking-for-kids/grade-1-2/',
    paymentPageUrl: null,
    curriculumPdf: PSC_COURSES_GRADE_1_2.pscgm_12.path,
    introVideoUrl: null,
    trialCourseSlug: 'public-speaking-trial-class',
  },
  {
    id: 2,
    title: 'Upper Elementary Grades 3-5',
    description:
      'Build confidence and clear communication skills by telling stories, sharing ideas, and speaking up in fun, creative ways. Practice writing and presenting with guidance, so every student feels ready to speak with clarity and confidence.',
    thumbnailLandscape:
      '/images/courses/public-speaking-and-communication/grade-3-5/listing-card.jpg',
    ageRange: 'Ages 8-11',
    gradeRange: 'Grades 3-5',
    estimatedCompletionTime: '1-24 months',
    studentsEnrolledCount: 0,
    totalLessonsCount: 144,
    tags: null,
    landingPageUrl: '/courses/public-speaking-for-kids/grade-3-5/',
    paymentPageUrl: null,
    curriculumPdf: PSC_COURSES_GRADE_3_5.pscgm_3_5.path,
    introVideoUrl: null,
    trialCourseSlug: 'public-speaking-trial-class',
  },
  {
    id: 3,
    title: 'Middle School Grades 6-8',
    description:
      'Strengthen your voice and sharpen your message. Learn to craft compelling speeches, participate in debates, and express ideas clearly both in writing and speaking. Perfect for students developing leadership and communication skills for school and beyond.',
    thumbnailLandscape:
      '/images/courses/public-speaking-and-communication/grade-6-8/listing-card.jpg',
    ageRange: 'Ages 11-14',
    gradeRange: 'Grades 6-8',
    estimatedCompletionTime: '1-24 months',
    studentsEnrolledCount: 0,
    totalLessonsCount: 144,
    tags: null,
    landingPageUrl: null,
    paymentPageUrl: null,
    curriculumPdf: PSC_COURSES_GRADE_6_8.pscgm_6_8.path,
    introVideoUrl: null,
    trialCourseSlug: 'public-speaking-trial-class',
  },
  {
    id: 4,
    title: 'Freshman and Sophomore Grades 9-10',
    description:
      'Master the art of persuasion and clear expression. Students work on advanced writing, public speaking, and presentation skills that build confidence, prepare for academic challenges, and help them stand out in any conversation or competition.',
    thumbnailLandscape:
      '/images/courses/public-speaking-and-communication/grade-9-10/listing-card.jpg',
    ageRange: 'Ages 14-16',
    gradeRange: 'Grades 9-10',
    estimatedCompletionTime: '1-24 months',
    studentsEnrolledCount: 0,
    totalLessonsCount: 144,
    tags: null,
    landingPageUrl: null,
    paymentPageUrl: null,
    curriculumPdf: null,
    introVideoUrl: null,
    trialCourseSlug: 'public-speaking-trial-class',
  },
  {
    id: 5,
    title: 'Junior and Senior Grades 11-12',
    description:
      'Refine your communication for real-world success. Focus on persuasive speaking, critical thinking, and professional presentations that prepare students for college, careers, and leadership roles where strong communication is key.',
    thumbnailLandscape:
      '/images/courses/public-speaking-and-communication/grade-11-12/listing-card.jpg',
    ageRange: 'Ages 16-18',
    gradeRange: 'Grades 11-12',
    estimatedCompletionTime: '1-24 months',
    studentsEnrolledCount: 0,
    totalLessonsCount: 144,
    tags: null,
    landingPageUrl: null,
    paymentPageUrl: null,
    curriculumPdf: null,
    introVideoUrl: null,
    trialCourseSlug: 'public-speaking-trial-class',
  },
]

export const PUBLIC_SPEAKING_HUB: ElaCourseContent = {
  firstFold: {
    badge: {
      icon: <Mic size={16} className="inline mr-2" />,
      text: 'Public speaking classes',
    },
    title: (
      <Trans>
        <span className="underlined-text">Public Speaking</span> and
        Communication classes for kids
      </Trans>
    ),
    description:
      'Help your child speak clearly, confidently, and with structure, in class and beyond. Live online lessons with expert teachers and a step-by-step pathway by grade.',
    courseQuery: 'public-speaking-trial-class',
    image: {
      src:
        '/images/courses/public-speaking-and-communication/first-fold-v2.png',
      alt: defineMessage({
        message: 'Children learning public speaking through online classes',
      }),
      width: 680,
      height: 522,
    },
  },
  pathways: {
    description:
      "From early storytelling to confident presentations, our program adapts to your child's grade level. Each stage helps students build vocabulary, structure their thoughts, and speak with clarity and confidence.",
    courses: PATHWAY_COURSES,
  },
  whySkillsMatter: {
    title: 'Why Public Speaking & Communication skills matter for your child',
    description:
      'Communication is one of the most important life skills a child can develop. When children learn to organize their thoughts, speak confidently, and present their ideas, they build the confidence needed to participate, lead, and succeed both in school and beyond.',
    items: [
      {
        icon: <AcademicCap className="h-8 w-8" />,
        title: 'Academic excellence',
        color: 'violet-100',
        description:
          'Students who express their ideas clearly perform better in presentations, class discussions, and interviews.',
      },
      {
        icon: <Users size={32} />,
        title: 'Social confidence',
        color: 'blue-200',
        description:
          'Students overcome shyness and learn to share their thoughts with confidence in any social setting.',
      },
      {
        icon: <Trophy className="h-8 w-8" />,
        title: 'Leadership development',
        color: 'green-200',
        description:
          'Strong communicators become natural leaders in school projects, clubs, and extracurricular activities.',
      },
      {
        icon: <Brain className="h-8 w-8" />,
        title: 'Future career readiness',
        color: 'red-200',
        description:
          'Public speaking is one of the most in-demand soft skills employers look for worldwide.',
      },
    ],
  },
  outcomes: {
    items: [
      {
        text: 'From quiet answers to clear full sentences',
        icon: <MessageSquare size={16} />,
        color: 'green-100',
      },
      {
        text: 'From rushing to calmer pace and pauses',
        icon: <Clock size={16} />,
        color: 'violet-100',
      },
      {
        text: 'From listening passively to engaging actively',
        icon: <Volume2 size={16} />,
        color: 'blue-200',
      },
      {
        text: 'From fear to confident starts and finishes',
        icon: <Target className="h-4 w-4" />,
        color: 'yellow-200',
      },
      {
        text: 'From nervous stumbles to smooth delivery',
        icon: <TrendingUp size={16} />,
        color: 'red-200',
      },
    ],
    image: {
      src:
        '/images/courses/public-speaking-and-communication/speaking-kids.png',
      alt: 'Kids building public speaking skills',
      width: 567,
      height: 567,
      className: 'rounded-full object-cover mx-auto',
    },
  },
  curriculum: {
    title: "Build your child's speaking confidence",
    description:
      'Our comprehensive curriculum covers every aspect of public speaking and communication, from foundational skills to advanced techniques.',
    items: [
      'Self-introduction & ice breakers',
      'Storytelling & narrative building',
      'Voice modulation & tone control',
      'Body language & stage presence',
      'Persuasive speaking & argumentation',
      'Debate & group discussion',
      'Presentation design & delivery',
      'Interview & audition preparation',
      'Creative expression & poetry',
      'Impromptu & extempore speaking',
    ],
    image: {
      src:
        '/images/courses/public-speaking-and-communication/speaking-confidence.png',
      alt: 'Kids in public speaking class',
      width: 619,
      height: 434,
      className: 'rounded-xl object-cover',
    },
  },
  howItWorks: {
    description:
      'Getting started is simple. Follow these four steps to set your child on the path to confident communication.',
    steps: [
      {
        step: 1,
        icon: Calendar,
        title: 'Book a free trial',
        description:
          "Schedule a complimentary session to assess your child's current level and learning goals.",
      },
      {
        step: 2,
        icon: BookOpen,
        title: 'Get a personalized plan',
        description:
          "Our expert instructors create a customized curriculum based on your child's age, interests, and skill level.",
      },
      {
        step: 3,
        icon: Play,
        title: 'Learn & practice',
        description:
          'Engage in live, interactive sessions with activities, role-plays, and real-world speaking opportunities.',
      },
      {
        step: 4,
        icon: BarChart2,
        title: 'Track progress',
        description:
          "Receive regular progress reports and watch your child's confidence grow week by week.",
      },
    ],
  },
}
